import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, createUserProfile } from '../lib/supabase/userProfiles';
import type { UserProfile, UserRole } from '../types/profiles';

export function useUserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsRole, setNeedsRole] = useState(false);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      setNeedsRole(false);
      return;
    }

    const loadProfile = async () => {
      try {
        const data = await getUserProfile(user.id);
        if (data) {
          setProfile(data);
          setNeedsRole(false);
        } else {
          setNeedsRole(true);
        }
      } catch {
        setNeedsRole(false);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const dismissRoleSelector = () => {
    setNeedsRole(false);
  };

  const selectRole = async (role: UserRole, companyName?: string) => {
    if (!user) return;
    try {
      const newProfile = await createUserProfile(user.id, role, companyName);
      setProfile(newProfile);
      setNeedsRole(false);
    } catch (err) {
      throw err;
    }
  };

  return { profile, loading, needsRole, selectRole, dismissRoleSelector };
}
